import { NOTIFY_DEFAULT_KINDS } from '@takeit4me/shared'
import { useSyncExternalStore } from 'react'
import type { NotificationView, NotifyChannel, NotifyKind } from '@takeit4me/shared'
import { api } from '../api'
import type { Tone } from './labels'
import { setAppBadge } from './push'

/**
 * The unread count behind the bell, shared by every page (each page has its own top bar). It is refreshed by
 * live 'notification' events, by pushes the service worker passes on, after any read or act, and by a
 * 60-second poll as a fallback. `version` goes up whenever something may have changed, so open lists reload.
 */
interface UnreadState { unread: number | null; version: number }
let state: UnreadState = { unread: null, version: 0 }
const subs = new Set<() => void>()
let timer: ReturnType<typeof setInterval> | null = null
let inflight: Promise<void> | null = null
const POLL_MS = 60_000

function set(next: Partial<UnreadState>) {
  state = { ...state, ...next }
  for (const s of subs) s()
}

/** Re-reads the unread count. `changed` also tells open lists to reload. */
export function refreshUnread(changed = true): Promise<void> {
  if (changed) set({ version: state.version + 1 })
  inflight ??= api.notifications({ unread: true, limit: 1 })
    .then((f) => { set({ unread: f.unread }); setAppBadge(f.unread) })
    .catch(() => { /* keep the last count; the next poll tries again */ })
    .finally(() => { inflight = null })
  return inflight
}

/** Sets the count straight away (after marking things read) before the server confirms it. */
export function adjustUnread(fn: (n: number) => number) {
  if (state.unread !== null) { const n = Math.max(0, fn(state.unread)); set({ unread: n }); setAppBadge(n) }
}

function onVisible() { if (document.visibilityState === 'visible') void refreshUnread(false) }

function subscribe(fn: () => void) {
  subs.add(fn)
  if (subs.size === 1) {
    void refreshUnread(false)
    timer = setInterval(() => { if (document.visibilityState !== 'hidden') void refreshUnread(false) }, POLL_MS)
    document.addEventListener('visibilitychange', onVisible)
  }
  return () => {
    subs.delete(fn)
    if (!subs.size) {
      if (timer) clearInterval(timer)
      timer = null
      document.removeEventListener('visibilitychange', onVisible)
    }
  }
}

export function useUnread(): UnreadState {
  return useSyncExternalStore(subscribe, () => state)
}

// ---------------------------------------------------------------- Labels
export const SEVERITY: Record<NotificationView['severity'], { label: string; tone: Tone; dot: string; bar: string }> = {
  critical: { label: 'Critical', tone: 'red', dot: 'bg-red-500', bar: 'border-l-red-500' },
  warning: { label: 'Warning', tone: 'amber', dot: 'bg-amber-500', bar: 'border-l-amber-400' },
  info: { label: 'Info', tone: 'blue', dot: 'bg-brand2', bar: 'border-l-indigo-300' },
}
export const severityOf = (s: string) => SEVERITY[s as NotificationView['severity']] ?? SEVERITY.info

/** The kinds a person can route (the 'test' kind always uses every channel). */
export const KINDS: { key: Exclude<NotifyKind, 'test'>; label: string; hint: string }[] = [
  { key: 'alarm', label: 'Alarms', hint: 'Something is wrong, such as a channel gone quiet' },
  { key: 'escalation', label: 'Escalations', hint: 'Nobody picked something up in time' },
  { key: 'handover', label: 'Handovers', hint: 'A customer is waiting live for a person' },
  { key: 'exception', label: 'New exceptions', hint: 'Items the AI needs a person for' },
  { key: 'assigned', label: 'Assigned to me', hint: 'A colleague gave you an item' },
  { key: 'offered', label: 'Offered to me', hint: 'Routing offered you an item' },
  { key: 'call', label: 'Calls', hint: 'A call is being put through to you' },
  { key: 'learning', label: 'Approvals and learning', hint: 'Changes to approve, autonomy earned or lost, roll-backs and audits' },
]
export const kindLabel = (k: string) => KINDS.find((x) => x.key === k)?.label ?? (k === 'test' ? 'Test' : k.charAt(0).toUpperCase() + k.slice(1))

/**
 * The channels each kind uses when the person has not chosen (mirrors DEFAULT_KINDS in
 * apps/api/src/services/notify.ts). A critical alarm also texts unless the person chose otherwise.
 */
/** The server's defaults (packages/shared NOTIFY_DEFAULT_KINDS), without the test kind. */
const { test: _test, ...KIND_DEFAULTS } = NOTIFY_DEFAULT_KINDS
export const DEFAULT_KIND_CHANNELS: Record<Exclude<NotifyKind, 'test'>, NotifyChannel[]> = KIND_DEFAULTS

export const CHANNELS: { key: NotifyChannel; label: string }[] = [
  { key: 'push', label: 'Push' },
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'Text' },
]
export const channelLabel = (c: string) => CHANNELS.find((x) => x.key === c)?.label ?? c

export function deliveryTone(status: string): { tone: Tone; label: string } {
  switch (status) {
    case 'sent': return { tone: 'green', label: 'sent' }
    case 'failed': return { tone: 'red', label: 'failed' }
    case 'skipped': return { tone: 'gray', label: 'skipped' }
    case 'queued': case 'sending': return { tone: 'amber', label: 'sending' }
    default: return { tone: 'gray', label: status }
  }
}
